<template>
  <article class="stock-match-card" :class="{ 'stock-match-card--reference': reference }">
    <div class="card-header">
      <p class="stock-match-card__title">{{ title }}</p>
      <img :src="imageUrl" :alt="displayName" class="stock-match-card__image" loading="lazy" />
      <p class="stock-match-card__model" :title="displayName">{{ displayName }}</p>
      <stock-match-price :price="price" :difference="priceDifference" />
      <p class="stock-match-card__engine" :title="engine">{{ engine }}</p>
      <div class="stock-match-card__origin">
        <template v-if="!reference">
          <p class="stock-match-card__origin-label">{{ origin }}</p>
          <p>{{ i18n.t('/stockmatch/komnumberlabel') }} {{ komNumber }}</p>
        </template>
      </div>
    </div>

    <stock-match-score
      class="stock-match-card__score"
      :reference="reference"
      :rows="rows"
      :extras="extras"
    />

    <p class="stock-match-card__label stock-match-card__rows-header">
      {{ i18n.t('/stockmatch/options-header') }} ({{ rows.length }})
    </p>
    <ul class="stock-match-card__rows">
      <li v-for="(row, index) in rows" :key="row.code + index">
        <stock-match-row :code="row.code" :name="row.name" :matches="row.matches" />
      </li>
    </ul>

    <div v-if="extras.length" class="stock-match-card__extras">
      <p class="stock-match-card__label">
        {{ i18n.t('/stockmatch/extrasheader', { count: extras.length }) }}
      </p>
      <ul class="stock-match-card__rows">
        <li v-for="extra in extras" :key="extra.code">
          <stock-match-row :code="extra.code" :name="extra.name" extra />
        </li>
      </ul>
    </div>

    <div class="stock-match-card__footer">
      <p v-if="reference" class="stock-match-card__note">
        <icon icon="info" :width="16" :height="16" />
        <span>{{ i18n.t('/stockmatch/referencenote') }}</span>
      </p>
      <template v-else>
        <ds-button
          variant="ghost"
          size="sm"
          block
          data-qa="stockcar-detail-button"
          @click="$emit('stockcar-details')"
        >
          {{ i18n.t('/stockmatch/stockcardetailbutton') }}
        </ds-button>
        <ds-button
          variant="ghost"
          size="sm"
          block
          data-qa="reservation-status-button"
          @click="$emit('reservation-status')"
        >
          {{ i18n.t('/stockmatch/reservationstatusbutton') }}
        </ds-button>
        <ds-button size="sm" block data-qa="new-quote-button" @click="$emit('select-car')">
          {{ i18n.t('/stockmatch/newquotebutton') }}
        </ds-button>
      </template>
    </div>
  </article>
</template>

<script>
import DsButton from '@/components/ds/atoms/dsButton/DsButton.vue'
import Icon from '@/components/generic/presentational/atoms/icon/Icon.vue'
import StockMatchPrice from '@/components/generic/presentational/molecules/stockMatchPrice/StockMatchPrice.vue'
import StockMatchRow from '@/components/generic/presentational/molecules/stockMatchRow/StockMatchRow.vue'
import StockMatchScore from '@/components/generic/presentational/molecules/stockMatchScore/StockMatchScore.vue'

export default {
  components: { DsButton, Icon, StockMatchPrice, StockMatchRow, StockMatchScore },
  props: {
    reference: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      required: true,
    },
    imageUrl: {
      type: String,
      // default is a 3:2 grey area
      default:
        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAMAAAACCAQAAAA3fa6RAAAAEElEQVR42mM8858BCBghFAAgNgOZPTV32QAAAABJRU5ErkJggg==',
    },
    displayName: {
      type: String,
      required: true,
    },
    price: {
      type: Number,
      required: true,
    },
    priceDifference: {
      type: Number,
      default: 0,
    },
    engine: {
      type: String,
      default: '',
    },
    origin: {
      type: String,
      default: '',
    },
    komNumber: {
      type: String,
      default: '',
    },
    rows: {
      type: Array,
      default: () => [],
    },
    extras: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['stockcar-details', 'reservation-status', 'select-car'],
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
    }
  },
}
</script>

<style lang="scss" scoped>
// Every block below has a fixed height so the rows of neighbouring cards line
// up: a dealer compares the same option across cards by reading left to right.
$row-height: 2.25rem;

.stock-match-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--ds-color-border);
  border-radius: var(--ds-radius-md);
  background: var(--ds-color-surface);
  font-size: var(--ds-font-size-base);
  color: var(--ds-color-text);
  // clip, not hidden: hidden would make the card the scroll container of the
  // modal's sticky card header, so the header would no longer stick.
  overflow: clip;

  p {
    margin: 0;
  }

  &--reference {
    border: 2px solid var(--ds-color-brand);
  }
}

.card-header {
  padding: 0 var(--ds-space-4);
  background: var(--ds-color-surface);
}

.stock-match-card__title {
  margin: 0 calc(-1 * var(--ds-space-4)) var(--ds-space-3);
  padding: var(--ds-space-3) var(--ds-space-4);
  border-bottom: 1px solid var(--ds-color-border-subtle);
  font-weight: 700;
  color: var(--ds-color-heading);

  .stock-match-card--reference & {
    background: var(--ds-color-accent-subtle);
  }
}

.stock-match-card__image {
  width: 100%;
  display: block;
  aspect-ratio: 3 / 2;
  object-fit: contain;
  border-radius: var(--ds-radius-sm);
  background: var(--ds-color-surface-sunken);
}

.stock-match-card__model {
  margin-top: var(--ds-space-3) !important;
  font-weight: 700;
}

.stock-match-card__model,
.stock-match-card__engine {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stock-match-card__engine {
  font-size: var(--ds-font-size-sm);
  color: var(--ds-color-text-muted);
}

.stock-match-card__origin {
  height: 3.5rem;
  margin-top: var(--ds-space-3);
  padding-top: var(--ds-space-2);
  border-top: 1px solid var(--ds-color-border-subtle);
  font-size: var(--ds-font-size-sm);
  color: var(--ds-color-text-muted);

  .stock-match-card--reference & {
    border-top-color: transparent;
  }
}

.stock-match-card__origin-label {
  font-weight: 700;
  color: var(--ds-color-text);
}

.stock-match-card__score {
  height: 5.5rem;
  margin: 0 var(--ds-space-4) var(--ds-space-4);
}

.stock-match-card__label {
  font-size: var(--ds-font-size-xs);
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ds-color-text-muted);
}

.stock-match-card__rows-header,
.stock-match-card__extras {
  padding: 0 var(--ds-space-4);
}

.stock-match-card__extras {
  margin-top: var(--ds-space-4);

  .stock-match-card__rows {
    padding: 0;
  }
}

.stock-match-card__rows {
  list-style: none;
  margin: var(--ds-space-1) 0 0;
  padding: 0 var(--ds-space-4);

  li {
    display: flex;
    align-items: center;
    height: $row-height;
    border-top: 1px solid var(--ds-color-border-subtle);
  }

  .stock-match-row {
    flex: 1;
  }
}

.stock-match-card__footer {
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-2);
  margin-top: auto;
  padding: var(--ds-space-4);
}

.stock-match-card__note {
  display: flex;
  gap: var(--ds-space-2);
  padding: var(--ds-space-3);
  border-radius: var(--ds-radius-md);
  background: var(--ds-color-surface-sunken);
  font-size: var(--ds-font-size-sm);
  color: var(--ds-color-text-muted);

  svg {
    flex-shrink: 0;
  }
}
</style>
